import React from 'react';
import { AccountMultipleDimension, DimensionData } from '~/types/internal-api';
import { DimensionDataItemDto } from '~/app/pages/accounting/bank/bank-reconciliation/types';
import hasFlag from '../util/utilities/hasFlag';
import SelectedDimensions from './components/SelectedDimensions';
import { Dimension, DimensionsAccess } from './types';

export const flattenDimensionIds = (value?: DimensionData[]) => {
    if (!value || value.length === 0) {
        return '';
    }
    // Would be nice to replace the hardcoded array with the real available dimensions
    const str = [1, 2, 3].map(item => value.find(x => x.dimensionId === item)?.dimensionKey ?? '').join(',');
    return str;
};

export const applyDefaultDimensions = (
    dbDimensions?: DimensionDataItemDto[],
    accountDimensions?: AccountMultipleDimension[],
    journalDimensions?: DimensionData[]
) => {
    const dimensionData: DimensionDataItemDto[] = [];
    // Would be nice to replace the hardcoded array with the real available dimensions
    [1, 2, 3].map(item => {
        if (dbDimensions?.some(x => x.DimensionId === item && x.DimensionKey)) {
            dimensionData.push({
                DimensionId: item,
                DimensionKey: dbDimensions.find(x => x.DimensionId === item && x.DimensionKey)!.DimensionKey,
            });
        } else if (accountDimensions?.some(x => x.dimensionId === item && x.dimensionKey)) {
            dimensionData.push({
                DimensionId: item,
                DimensionKey: accountDimensions.find(x => x.dimensionId === item && x.dimensionKey)!.dimensionKey,
            });
        } else if (journalDimensions?.some(x => x.dimensionId === item && x.dimensionKey)) {
            dimensionData.push({
                DimensionId: item,
                DimensionKey: journalDimensions.find(x => x.dimensionId === item && x.dimensionKey)!.dimensionKey,
            });
        }
        return true;
    });
    return dimensionData;
};

export const applyJournalDefaultDimensions = (
    formDimensions?: DimensionDataItemDto[],
    accountDimensions?: AccountMultipleDimension[],
    journalDimensions?: DimensionData[]
) => {
    const dimensionData: DimensionDataItemDto[] = [];
    [1, 2, 3].map(item => {
        if (!accountDimensions?.some(x => x.dimensionId === item && x.dimensionKey)) {
            if (formDimensions?.some(x => x.DimensionId === item && x.DimensionKey)) {
                dimensionData.push({
                    DimensionId: item,
                    DimensionKey: formDimensions.find(x => x.DimensionId === item && x.DimensionKey)!.DimensionKey,
                });
            } else if (journalDimensions?.some(x => x.dimensionId === item && x.dimensionKey)) {
                dimensionData.push({
                    DimensionId: item,
                    DimensionKey: journalDimensions.find(x => x.dimensionId === item && x.dimensionKey)!.dimensionKey,
                });
            } else {
                dimensionData.push({
                    DimensionId: item,
                    DimensionKey: undefined,
                });
            }
        }
        return true;
    });

    return dimensionData;
};

// Only used in current implementation of TP, can be deleted after new version is released
export const applyAccountDefaultDimensions = (
    formDimensions?: DimensionDataItemDto[],
    accountDimensions?: AccountMultipleDimension[]
) => {
    const dimensionData: DimensionDataItemDto[] = [];
    [1, 2, 3].map(item => {
        if (accountDimensions?.some(x => x.dimensionId === item && x.dimensionKey)) {
            const dk = accountDimensions.find(x => x.dimensionId === item && x.dimensionKey)!.dimensionKey;
            dimensionData.push({
                DimensionId: item,
                DimensionKey: dk,
            });
        } else if (formDimensions?.some(x => x.DimensionId === item && x.DimensionKey)) {
            const dk = formDimensions.find(x => x.DimensionId === item && x.DimensionKey)!.DimensionKey;
            dimensionData.push({
                DimensionId: item,
                DimensionKey: dk,
            });
        }
        return true;
    });
    return dimensionData;
};

// Only used in current implementation of TP, can be deleted after new version is released
export const fillInDimensionData = (dimensions: Dimension[], dimensionData: { DimensionId: number; DimensionKey?: number }[]) => {
    return dimensions.map(
        dim =>
            dimensionData.find(d => d.DimensionId === dim.id) || {
                DimensionId: dim.id,
                DimensionKey: undefined,
            }
    );
};

export interface AllDimensionsData {
    allDimensions: Dimension[];
    areAllDimensionsLoading: boolean;
    getDimensionName?: (dimensionId: number, substitute: string) => string;
}

export const multipleDimensionsColumnRenderer = (
    { allDimensions, areAllDimensionsLoading }: AllDimensionsData | undefined = {
        allDimensions: [],
        areAllDimensionsLoading: false,
    },
    value?: DimensionData[]
) => {
    if (!value || value.length === 0) {
        return null;
    }

    return <SelectedDimensions data={value} allDimensions={allDimensions} isLoading={areAllDimensionsLoading} />;
};

export const getDimensionColumns = (
    dimensionsAccessLevel: DimensionsAccess,
    dimensions: Dimension[]
): { accessor: string; columnHeader: string }[] => {
    if (hasFlag(dimensionsAccessLevel, DimensionsAccess.HasDimension)) {
        return dimensions.map(dim => ({ accessor: `dimension${dim.id}Value`, columnHeader: dim.name }));
    }

    // No access to dimensions
    return [];
};
